import React, { useState } from 'react';
import {
  get, uniqBy,
} from 'lodash';
import {
  Avatar, IconButton, Popover, useMediaQuery, Tooltip,
} from '@mui/material';
import Close from '@mui/icons-material/Close';
import { useTheme } from '@mui/material/styles';
import { useRouter } from 'next/router';
import { ParsedUrlQuery } from 'querystring';
import { updateRouterQuery } from 'lib/utils/filterUtils';
import { Credit } from 'lib/types/credits';
import { imageAvatarUrl, PRIVATE_CREDIT_AVATAR } from 'lib/utils/imageUtils';
import { CreditItem } from 'components/CreditItem';

const AVATAR_SIZE = 30;

interface Props {
  credits: Credit[];
}

export const CreditsCol = ({
  credits,
}: Props) => {
  const router = useRouter();
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const open = Boolean(anchorEl);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const onOpen = (event: React.MouseEvent<HTMLElement>) => {
    if (credits?.length > 0) {
      setAnchorEl(event.currentTarget);
    }
  };

  const onClose = () => {
    setAnchorEl(null);
  };

  const onClick = (query: ParsedUrlQuery) => {
    updateRouterQuery(router, query);
  };

  const renderCreditAvatars = () => {
    const creditsDisplayed = uniqBy(credits, 'userId');
    return (
      <>
        {creditsDisplayed.map((credit) => {
          const user = get(credit, 'user');
          const avatarUrl = credit.isPrivate
            ? PRIVATE_CREDIT_AVATAR : imageAvatarUrl(user?.avatarUrl);

          if (credit.isHidden) {
            return null;
          }

          return (
            <Tooltip
              title={(
                <>
                  <div className="text-sm font-semibold">{user?.name}</div>
                  {credit.role.name}
                </>
              )}
              key={credit?.id}
              arrow
            >
              <Avatar
                className="mr-1 hover:border-2 hover:border-orange"
                sx={{ width: AVATAR_SIZE, height: AVATAR_SIZE }}
                key={credit.id}
                variant="circular"
                src={avatarUrl}
                alt={user?.name || ''}
                imgProps={{ loading: 'lazy' }}
                component="button"
                onClick={() => onClick({ users: `${user?.username}~${user?.id}` })}
              />

            </Tooltip>
          );
        })}
      </>
    );
  };

  return (
    <>
      <div
        className="flex flex-wrap min-w-[110px] gap-1"
        aria-describedby="credit-preview"
      >
        {renderCreditAvatars()}
      </div>
      {credits.length > 0 && (
        <div className="flex mt-2">
          <button
            type="button"
            onClick={onOpen}
            className="btn-primary-outlined-xs mr-1"
          >
            View
          </button>
        </div>
      )}
      <Popover
        id="credit-preview"
        open={open}
        anchorEl={anchorEl}
        {...(!isMobile && { sx: { pointerEvents: 'none' } })}
        onClose={onClose}
        PaperProps={{
          sx: {
            pointerEvents: 'auto',
            maxWidth: 300,
          },
          onMouseEnter: onOpen,
          onMouseLeave: onClose,
        }}
      >
        <div className="p-4">
          <div className="absolute right-2 top-2">
            <IconButton
              aria-label="close"
              onClick={onClose}
            >
              <Close />
            </IconButton>
          </div>
          {' '}
          <div className="mb-2">
            {credits.map(
              (credit) => (
                <CreditItem
                  key={credit.id}
                  credit={credit}
                />
              ),
            )}
          </div>
        </div>
      </Popover>
    </>
  );
};
